// Lightweight sequence loader with caching and UniProt fallback

export type SequenceMap = Record<string, string>;

const SEQ_STORE_KEY = 'csea_protein_sequences_v1';
const MEM_CACHE: Map<string, string> = new Map();
let STATIC_MAP: SequenceMap | null = null;
let STATIC_LOAD_PROMISE: Promise<void> | null = null;

function saveLocalCache() {
  try {
    const obj: Record<string, string> = {};
    MEM_CACHE.forEach((v, k) => (obj[k] = v));
    localStorage.setItem(SEQ_STORE_KEY, JSON.stringify(obj));
  } catch (_) {
    // ignore
  }
}

function loadLocalCache() {
  try {
    const raw = localStorage.getItem(SEQ_STORE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw) as Record<string, string>;
    Object.entries(obj).forEach(([k, v]) => MEM_CACHE.set(k, v));
  } catch (_) {
    // ignore
  }
}

loadLocalCache();

export async function loadStaticSequenceMap(): Promise<void> {
  if (STATIC_LOAD_PROMISE) return STATIC_LOAD_PROMISE;
  STATIC_LOAD_PROMISE = (async () => {
    try {
      const res = await fetch('/data/protein_sequences.json');
      if (res.ok) {
        STATIC_MAP = (await res.json()) as SequenceMap;
      }
    } catch (_) {
      // no static map available
    }
  })();
  return STATIC_LOAD_PROMISE;
}

function isAccession(id: string): boolean {
  // Simple UniProt accession regex
  return /^[OPQ][0-9][A-Z0-9]{3}[0-9]$/.test(id) || /^[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2}$/.test(id);
}

function baseAccession(id: string): string {
  // Strip isoform suffix (e.g., P12345-2 -> P12345)
  const parts = id.split('-');
  return parts[0];
}

async function resolveGeneToAccession(gene: string): Promise<string | null> {
  try {
    const url = `https://rest.uniprot.org/uniprotkb/search?query=gene_exact:${encodeURIComponent(
      gene
    )}+AND+organism_id:9606+AND+reviewed:true&fields=accession&size=1`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const acc = data?.results?.[0]?.primaryAccession;
    return typeof acc === 'string' ? acc : null;
  } catch {
    return null;
  }
}

async function fetchAccessionSequence(accession: string): Promise<string | null> {
  try {
    const res = await fetch(`https://rest.uniprot.org/uniprotkb/${accession}.json`);
    if (!res.ok) return null;
    const data = await res.json();
    const seq = data?.sequence?.value;
    if (typeof seq === 'string' && seq.length > 0) return seq;
    return null;
  } catch {
    return null;
  }
}

export async function getSequence(rawId: string): Promise<string | null> {
  if (!rawId) return null;
  const id = rawId.trim();

  // Memory cache
  if (MEM_CACHE.has(id)) return MEM_CACHE.get(id)!;

  await loadStaticSequenceMap();
  if (STATIC_MAP && STATIC_MAP[id]) {
    MEM_CACHE.set(id, STATIC_MAP[id]);
    saveLocalCache();
    return STATIC_MAP[id];
  }

  // Try accession or resolve
  let accession = id;
  if (!isAccession(accession)) {
    accession = (await resolveGeneToAccession(accession)) || accession;
  }
  accession = baseAccession(accession);

  // Check static by accession
  if (STATIC_MAP && STATIC_MAP[accession]) {
    MEM_CACHE.set(id, STATIC_MAP[accession]);
    saveLocalCache();
    return STATIC_MAP[accession];
  }

  // Fetch from UniProt
  const seq = await fetchAccessionSequence(accession);
  if (seq) {
    MEM_CACHE.set(id, seq);
    MEM_CACHE.set(accession, seq);
    saveLocalCache();
    return seq;
  }

  return null;
}

